/**
 * Resolving an asoebi record's `fabric_id` to the row it actually names.
 *
 * `asoebi_orders.fabric_id` / `asoebi_assignments.fabric_id` are overloaded:
 * each holds EITHER a bundle id or a fabric-typed product id. The two id
 * spaces overlap on live data — bundle 4 and bundle 7 collide with unrelated
 * products 4 and 7 — so a hit in either table proves nothing about which was
 * meant, and the "try one, fall back to the other" guess that used to be
 * re-improvised at every call site is wrong exactly where it matters.
 *
 * `fabric_kind` is the record's own answer (see gg-main#90 / ans-main#331) and
 * is authoritative. The id lookup survives here only as a fallback for a row
 * written before that column existed; it keeps the historical precedence —
 * bundles win — so pre-`fabric_kind` rows read the way they always did.
 */
import { FabricKind } from '../types/ui/Asoebi';

/** Anything that points at a fabric-or-bundle id: an order, an assignment. */
export interface FabricRef {
  fabric_id?: string | number | null;
  fabric_kind?: FabricKind | null;
}

/** Minimal shape of a fabric or bundle row, so this helper stays usable from
 *  anywhere without importing either catalogue's full type. */
export interface NamedRow {
  id: string;
  name: string;
}

/** `fabric_id` as a string, '' when the record points at nothing. */
export const fabricRefId = (ref: FabricRef): string =>
  ref.fabric_id == null ? '' : String(ref.fabric_id);

/**
 * Which table this record's `fabric_id` names.
 *
 * @param bundles only consulted for a record with no `fabric_kind`.
 */
export function fabricRefKind(ref: FabricRef, bundles: readonly NamedRow[]): FabricKind {
  if (ref.fabric_kind) return ref.fabric_kind;
  const id = fabricRefId(ref);
  return id && bundles.some(b => String(b.id) === id) ? 'bundle' : 'product';
}

/** True when this record's `fabric_id` names a bundle. */
export const isBundleRef = (ref: FabricRef, bundles: readonly NamedRow[]): boolean =>
  fabricRefKind(ref, bundles) === 'bundle';

/** True when this record names *this specific* bundle — an id match alone
 *  would also sweep in a direct-fabric record whose product id collides. */
export const refersToBundle = (
  ref: FabricRef,
  bundleId: string | number,
  bundles: readonly NamedRow[],
): boolean => fabricRefId(ref) === String(bundleId) && isBundleRef(ref, bundles);

/** True when this record names *this specific* fabric — again, an id match
 *  alone would sweep in a bundle assignment whose bundle id collides. */
export const refersToFabric = (
  ref: FabricRef,
  fabricId: string | number,
  bundles: readonly NamedRow[],
): boolean => fabricRefId(ref) === String(fabricId) && !isBundleRef(ref, bundles);

/** The bundle or fabric row this record names, or undefined when the id
 *  matches nothing in the table `fabric_kind` points at. */
export function resolveFabricRef<F extends NamedRow, B extends NamedRow>(
  ref: FabricRef,
  fabrics: readonly F[],
  bundles: readonly B[],
): { kind: FabricKind; fabric?: F; bundle?: B } {
  const id = fabricRefId(ref);
  const kind = fabricRefKind(ref, bundles);
  return kind === 'bundle'
    ? { kind, bundle: bundles.find(b => String(b.id) === id) }
    : { kind, fabric: fabrics.find(f => String(f.id) === id) };
}

/** Display name for whatever this record names. */
export function fabricRefName(
  ref: FabricRef,
  fabrics: readonly NamedRow[],
  bundles: readonly NamedRow[],
  fallback = 'Unknown',
): string {
  const { fabric, bundle } = resolveFabricRef(ref, fabrics, bundles);
  return (bundle ?? fabric)?.name ?? fallback;
}
